import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { LightboxImage } from '#/shared/ui/image-lightbox'

import type { ChatPhoto, OpenImageHandler } from './conversation'

interface ThreadLightbox {
  /** Open the gallery on a message's photos, focused on the tapped one. */
  openImage: OpenImageHandler
  /** Spread onto `<ImageLightbox />`. */
  lightboxProps: {
    images: ReadonlyArray<LightboxImage>
    index: number | null
    onIndexChange: (index: number) => void
    onClose: () => void
    closeLabel: string
    prevLabel: string
    nextLabel: string
    counter?: string
  }
}

/**
 * Drives the full-screen photo viewer for a conversation thread: tracks the
 * open message's photos and the focused index, and derives the labelled props
 * the shared lightbox needs.
 */
export function useThreadLightbox(): ThreadLightbox {
  const { t } = useTranslation()
  const [state, setState] = useState<{
    photos: ReadonlyArray<ChatPhoto>
    index: number
  } | null>(null)

  const images = useMemo<ReadonlyArray<LightboxImage>>(
    () =>
      (state?.photos ?? []).map((photo) => ({
        src: photo.url,
        alt: photo.name || t('phonePanel.conversation.photo'),
        // Sender + time line (DEV-700); photos without one keep the file-name
        // caption. Built captions always end with the time, so never empty.
        caption: photo.caption ?? (photo.name || undefined),
      })),
    [state, t],
  )

  return {
    openImage: (photos, index) => setState({ photos, index }),
    lightboxProps: {
      images,
      index: state?.index ?? null,
      onIndexChange: (index) =>
        setState((prev) => (prev ? { ...prev, index } : prev)),
      onClose: () => setState(null),
      closeLabel: t('phonePanel.conversation.closePhoto'),
      prevLabel: t('phonePanel.conversation.prevPhoto'),
      nextLabel: t('phonePanel.conversation.nextPhoto'),
      counter: state
        ? t('phonePanel.conversation.photoCounter', {
            current: state.index + 1,
            total: state.photos.length,
          })
        : undefined,
    },
  }
}
